import type React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { OwnerLayout } from '@/components/layout/OwnerLayout';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { SchoolApplyPage } from '@/features/public/pages/SchoolApplyPage';
import { PrivacyPage } from '@/features/public/pages/PrivacyPage';

// Auth
import { LoginPage } from '@/features/auth/pages/LoginPage';
import { AccountPage } from '@/features/auth/pages/AccountPage';
import { OtpPage } from '@/features/auth/pages/OtpPage';
import { VerifyPhonePage } from '@/features/auth/pages/VerifyPhonePage';
import { ChangePasswordPage } from '@/features/auth/pages/ChangePasswordPage';
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage';
import { ApplyLandingPage } from '@/features/auth/pages/ApplyLandingPage';
import { ApplyRegisterPage } from '@/features/auth/pages/ApplyRegisterPage';

// Owner pages
import { OwnerDashboardPage } from '@/features/owner/pages/OwnerDashboardPage';
import { BillingPage } from '@/features/owner/pages/BillingPage';

// Teacher tools reused by owner (owner has super-access to teacher endpoints)
import { QuestionBanksPage } from '@/features/owner/pages/QuestionBanksPage';
import { LoginsPage } from '@/features/shared/people/LoginsPage';
import { ScoreLocksPage } from '@/features/shared/results/ScoreLocksPage';
import { CbtCreatePage as TeacherCbtCreatePage } from '@/features/teacher/pages/CbtCreatePage';
import { OwnerCbtExamsListPage } from '@/features/owner/pages/OwnerCbtExamsListPage';
import { OwnerCbtCreatePage } from '@/features/owner/pages/OwnerCbtCreatePage';
import { OwnerCbtExamDetailPage } from '@/features/owner/pages/OwnerCbtExamDetailPage';
import { LiveMonitorPage as TeacherLiveMonitorPage } from '@/features/teacher/pages/LiveMonitorPage';
import { CbtResultsPage as TeacherCbtResultsPage } from '@/features/teacher/pages/CbtResultsPage';
import { OwnerImportScoresPage } from '@/features/owner/pages/OwnerImportScoresPage';
import { CbtPushPage as TeacherCbtPushPage } from '@/features/teacher/pages/CbtPushPage';
import { RemarksPage } from '@/features/owner/pages/RemarksPage';
import { ClassResultsPage } from '@/features/owner/pages/ClassResultsPage';
import { AdmissionsPage } from '@/features/owner/pages/AdmissionsPage';
import { StudentsPage } from '@/features/owner/pages/StudentsPage';
import { StudentProfilePage } from '@/features/owner/pages/StudentProfilePage';
import { FamiliesPage } from '@/features/owner/pages/FamiliesPage';
import { AcademicsPage } from '@/features/owner/pages/AcademicsPage';
import { ClassesArmsPage } from '@/features/owner/pages/ClassesArmsPage';
import { SubjectsPage } from '@/features/owner/pages/SubjectsPage';
import { TeacherAssignmentsPage } from '@/features/owner/pages/TeacherAssignmentsPage';
import { FeesPage } from '@/features/owner/pages/FeesPage';
import { ResultsPage } from '@/features/owner/pages/ResultsPage';
import { BroadsheetPage } from '@/features/owner/pages/BroadsheetPage';
import { GradingPage } from '@/features/owner/pages/GradingPage';
import { SessionsPage } from '@/features/owner/pages/SessionsPage';
import { SheetDesignerPage } from '@/features/owner/pages/SheetDesignerPage';
import { SignatoriesPage } from '@/features/owner/pages/SignatoriesPage';
import { ScoreEntryPage } from '@/features/owner/pages/ScoreEntryPage';
import { PublishPage } from '@/features/owner/pages/PublishPage';
import { TimetablePage } from '@/features/owner/pages/TimetablePage';
import { StaffPage } from '@/features/owner/pages/StaffPage';
import { WithdrawalsPage } from '@/features/owner/pages/WithdrawalsPage';
import { StaffAttendancePage } from '@/features/shared/people/StaffAttendancePage';
import { DeskAccessPage, BursarAccessPage, TeacherAccessPage, ParentAccessPage, StudentAccessPage, ApplicantAccessPage } from '@/features/owner/pages/DeskAccessPage';
import { ApprovalsPage } from '@/features/shared/finance/ApprovalsPage';
import { FeeStructurePage } from '@/features/bursar/pages/FeeStructurePage';
import { RecordPaymentPage } from '@/features/bursar/pages/RecordPaymentPage';
import { PaymentsPage } from '@/features/bursar/pages/PaymentsPage';
import { ReceiptsPage } from '@/features/bursar/pages/ReceiptsPage';
import { DebtorsPage } from '@/features/bursar/pages/DebtorsPage';
import { PayrollPage } from '@/features/owner/pages/PayrollPage';
import { BooksPage } from '@/features/owner/pages/BooksPage';
import { StockPage } from '@/features/bursar/pages/StockPage';
import { ReconciliationPage } from '@/features/bursar/pages/ReconciliationPage';
import { ExpensesPage } from '@/features/owner/pages/ExpensesPage';
import { RolesPage } from '@/features/owner/pages/RolesPage';
import { CompositeSubjectsPage } from '@/features/owner/pages/CompositeSubjectsPage';
import { SubjectRegistrationPage } from '@/features/owner/pages/SubjectRegistrationPage';
import { AttendancePage } from '@/features/owner/pages/AttendancePage';
import { OwnerWalletPage } from '@/features/owner/pages/OwnerWalletPage';
import { SettingsPage } from '@/features/owner/pages/OwnerSettingsPages';
import { HeldResultsPage } from '@/features/shared/results/HeldResultsPage';
import { LessonsStudio } from '@/features/shared/teaching/LessonsStudio';
import { LiveClassesPage } from '@/features/shared/teaching/LiveClassesPage';
import { LessonNotesPage } from '@/features/shared/teaching/LessonNotesPage';
import { AuditTrailPage } from '@/features/shared/support/AuditTrailPage';
import { FeesTranscriptsPage } from '@/features/owner/pages/FeesTranscriptsPage';
import { SchoolReportsPage } from '@/features/shared/reports/SchoolReports';
import { AlumniPage as AlumniDirectoryPage } from '@/features/desk/pages/AlumniPage';
import { Link as RLink } from 'react-router-dom';
import { StaffMessagesPage } from '@/features/shared/support/StaffMessagesPage';

const Padded = ({ children }: { children: React.ReactNode }) => <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6">{children}</div>;
import { BrandingPage } from '@/features/owner/pages/BrandingPage';
import { BirthdaysPage } from '@/features/owner/pages/BirthdaysPage';
import { CalendarPage } from '@/features/owner/pages/CalendarPage';
import { NoticesPage } from '@/features/owner/pages/NoticesPage';
import { PromotionPage } from '@/features/owner/pages/PromotionPage';
import { SupportPage } from '@/features/owner/pages/SupportPage';

// Applicant + Desk
import { applicantRoutes } from '@/features/applicant/routes';
import { deskRoutes, DeskLayout } from '@/features/desk/routes';
import { teacherRoutes } from '@/features/teacher/routes';
import { studentRoutes } from '@/features/student/routes';
import { bursarRoutes } from '@/features/bursar/routes';
import { parentRoutes } from '@/features/parent/routes';
import { RoleLayout } from '@/components/layout/RoleLayout';
import { nav as applicantNav } from '@/features/applicant/nav';
import { isNativeApp } from '@/lib/native';

function ApplicantLayout() {
  return <RoleLayout title="Applicant" base="/applicant" nav={applicantNav} />;
}

export function AppRouter() {
  return (
    <Routes>
      {/* Public auth */}
      <Route path="/schoolsapply" element={isNativeApp ? <Navigate to="/login" replace /> : <SchoolApplyPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/otp" element={<OtpPage />} />
      <Route path="/verify-phone" element={<VerifyPhonePage />} />
      <Route path="/change-password" element={<ChangePasswordPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/apply" element={<ApplyLandingPage />} />
      <Route path="/apply/register" element={<ApplyRegisterPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/delete-account" element={<PrivacyPage />} />

      {/* ─── OWNER (grouped sidebar) ─── */}
      <Route
        path="/owner"
        element={<RequireAuth roles={['owner']}><OwnerLayout /></RequireAuth>}
      >
        <Route index element={<Navigate to="dashboard" replace />} />

        {/* Overview */}
        <Route path="dashboard" element={<OwnerDashboardPage />} />

        {/* Teaching tools — reused from teacher surface */}
        <Route path="lessons" element={<LessonsStudio />} />
        <Route path="live-classes" element={<LiveClassesPage />} />
        <Route path="lesson-notes" element={<LessonNotesPage />} />
        <Route path="question-banks" element={<QuestionBanksPage />} />
        <Route path="logins" element={<LoginsPage />} />
        <Route path="score-locks" element={<ScoreLocksPage />} />
        <Route path="cbt-exams" element={<OwnerCbtExamsListPage />} />
        <Route path="cbt-exams/new" element={<OwnerCbtCreatePage />} />
        <Route path="cbt-exams/:id" element={<OwnerCbtExamDetailPage />} />
        <Route path="live-monitor" element={<TeacherLiveMonitorPage />} />
        <Route path="cbt-results" element={<TeacherCbtResultsPage />} />
        <Route path="import-scores" element={<OwnerImportScoresPage />} />
        <Route path="send-cbt-scores" element={<TeacherCbtPushPage />} />
        <Route path="remarks" element={<RemarksPage />} />
        <Route path="class-results" element={<ClassResultsPage />} />

        {/* School */}
        <Route path="admissions" element={<AdmissionsPage />} />
        <Route path="students" element={<StudentsPage />} />
        <Route path="promotions" element={<PromotionPage />} />
        <Route path="reports" element={<Padded><SchoolReportsPage /></Padded>} />
        <Route path="support" element={<SupportPage />} />
        <Route path="students/:id" element={<StudentProfilePage />} />
        <Route path="families" element={<FamiliesPage />} />
        <Route path="sessions" element={<SessionsPage />} />
        <Route path="classes" element={<ClassesArmsPage />} />
        <Route path="subjects" element={<SubjectsPage />} />
        <Route path="timetable" element={<TimetablePage />} />

        {/* Academics */}
        <Route path="academics" element={<AcademicsPage />} />
        <Route path="subject-registration" element={<SubjectRegistrationPage />} />
        <Route path="composite-subjects" element={<CompositeSubjectsPage />} />
        <Route path="attendance" element={<AttendancePage />} />

        {/* Assessments & Results */}
        <Route path="assessments" element={<Navigate to="/owner/cbt-exams" replace />} />
        <Route path="grading" element={<GradingPage />} />
        <Route path="score-entry" element={<ScoreEntryPage />} />
        <Route path="results" element={<ResultsPage />} />
        <Route path="broadsheet" element={<BroadsheetPage />} />
        <Route path="publish" element={<PublishPage />} />
        <Route path="sheet-designer" element={<SheetDesignerPage />} />
        <Route path="signatories" element={<SignatoriesPage />} />

        {/* Finance */}
        <Route path="fees" element={<Padded><FeesPage /></Padded>} />
        <Route path="wallet" element={<OwnerWalletPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="withdrawals" element={<WithdrawalsPage />} />
        <Route path="expenses" element={<ExpensesPage />} />

        {/* People & Access */}
        <Route path="staff" element={<StaffPage />} />
        <Route path="roles" element={<RolesPage />} />
        <Route path="assignments" element={<TeacherAssignmentsPage />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="books" element={<BooksPage />} />
        <Route path="stock" element={<Padded><StockPage /></Padded>} />
        <Route path="reconciliation" element={<ReconciliationPage />} />
        <Route path="clock-in" element={<StaffAttendancePage />} />
        <Route path="desk-access" element={<DeskAccessPage />} />
        <Route path="bursar-access" element={<BursarAccessPage />} />
        <Route path="teacher-access" element={<TeacherAccessPage />} />
        <Route path="parent-access" element={<ParentAccessPage />} />
        <Route path="student-access" element={<StudentAccessPage />} />
        <Route path="applicant-access" element={<ApplicantAccessPage />} />
        <Route path="invoices" element={<Padded><FeesPage initialTab="invoices" /></Padded>} />
        <Route path="approvals" element={<ApprovalsPage />} />
        <Route path="discounts" element={<Padded><FeeStructurePage /></Padded>} />
        <Route path="record-payment" element={<Padded><RecordPaymentPage /></Padded>} />
        <Route path="payments" element={<Padded><PaymentsPage /></Padded>} />
        <Route path="receipts" element={<Padded><ReceiptsPage /></Padded>} />
        <Route path="debtors" element={<Padded><DebtorsPage /></Padded>} />

        {/* Reports & Settings */}
        <Route path="settings" element={<Padded><SettingsPage /></Padded>} />
        <Route path="account" element={<Padded><AccountPage /></Padded>} />
        <Route path="held-results" element={<HeldResultsPage />} />
        <Route path="messages" element={<StaffMessagesPage />} />
        <Route path="alumni" element={<Padded>
          <p className="text-xs text-slate-500 mb-2">Prices and rules for these documents: <RLink to="/owner/fees-transcripts" className="font-semibold text-[#0A2540] underline">Fees & Transcripts</RLink></p>
          <AlumniDirectoryPage />
        </Padded>} />
        <Route path="branding" element={<BrandingPage />} />
        <Route path="audit-log" element={<AuditTrailPage />} />
        <Route path="fees-transcripts" element={<FeesTranscriptsPage />} />
        <Route path="birthdays" element={<BirthdaysPage />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="notices" element={<NoticesPage />} />

        {/* Catch-all for /owner/* unknown → dashboard */}
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Route>

      {/* ─── APPLICANT ─── */}
      <Route
        path="/applicant"
        element={<RequireAuth roles={['applicant']}><ApplicantLayout /></RequireAuth>}
      >
        {applicantRoutes()}
      </Route>

      {/* ─── DESK ─── */}
      <Route
        path="/desk"
        element={<RequireAuth roles={['desk', 'owner']}><DeskLayout /></RequireAuth>}
      >
        {deskRoutes()}
      </Route>

      {/* ─── TEACHER / STUDENT / BURSAR / PARENT ─── */}
      <Route path="/teacher/*" element={<RequireAuth roles={['teacher']}>{teacherRoutes()}</RequireAuth>} />
      <Route path="/student/*" element={<RequireAuth roles={['student']}>{studentRoutes()}</RequireAuth>} />
      <Route path="/bursar/*" element={<RequireAuth roles={['bursar', 'owner']}>{bursarRoutes()}</RequireAuth>} />
      <Route path="/parent/*" element={<RequireAuth roles={['parent']}>{parentRoutes()}</RequireAuth>} />

      {/* Root + 404 */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
